/**
 * TYPING-DELIVERY-VERIFY-001 — blocking "final report could not be delivered" dialog
 * shown to the Typing Processor when /typing/complete rejects the completion
 * (size pre-flight 400, SES rejection 409). The order stays in Typing.
 */
import React from 'react';
import { AlertTriangle, FileWarning, MailX, ServerCrash } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const REASONS = {
  size_limit: { icon: FileWarning, title: 'Final report exceeds the email size limit',
    hint: 'Reduce or split the Primary Search Document (15 MB per email), then complete the order again.' },
  recipient: { icon: MailX, title: 'Client delivery email rejected',
    hint: 'Verify the client delivery email on the Client Code / order, then complete again.' },
  ses_rejected: { icon: ServerCrash, title: 'Email service rejected the message',
    hint: 'Retry in a few minutes. If it keeps failing, contact your Team Leader/Admin.' },
  other: { icon: AlertTriangle, title: 'Final report could not be delivered',
    hint: 'Contact your Team Leader/Admin or take the required corrective action, then complete again.' },
};

export const parseDeliveryFailure = (err) => {
  const d = err?.response?.data?.detail;
  if (d && typeof d === 'object' && /^DELIVERY_/.test(d.code || '')) return d;
  return null;
};

export const DeliveryFailureDialog = ({ failure, onClose }) => {
  if (!failure) return null;
  const r = REASONS[failure.reason] || REASONS.other;
  const Icon = r.icon;
  return (
    <AlertDialog open onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <AlertDialogContent className="max-w-lg" data-testid="delivery-failure-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <Icon className="h-5 w-5" />
            </span>
            <AlertDialogTitle data-testid="delivery-failure-title">{r.title}</AlertDialogTitle>
          </div>
          <AlertDialogDescription asChild>
            <div className="space-y-3 pt-2 text-sm text-slate-600">
              <p data-testid="delivery-failure-message">{failure.message}</p>
              {failure.error && failure.reason !== 'size_limit' && (
                <p className="rounded-md border border-rose-100 bg-rose-50 px-3 py-2 font-mono text-[11px] text-rose-700 break-words"
                  data-testid="delivery-failure-error">{failure.error}</p>
              )}
              <p className="text-slate-500">{r.hint}{failure.recipient ? ` Recipient: ${failure.recipient}.` : ''}</p>
              <p className="font-medium text-slate-700">The order has NOT been marked completed — it remains in Typing.</p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={onClose} data-testid="delivery-failure-close">Understood</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeliveryFailureDialog;
